import React, {createContext, useCallback, useContext, useMemo, useState} from 'react';

import {useTeamsQuery} from './queries/useTeamsQuery';

interface TeamSelectionValue {
  /** Équipe actuellement consultée, ou `undefined` en compte personnel. */
  selectedTeamId: string | undefined;
  /**
   * Membres retenus dans le filtre de l'équipe courante. `undefined` = tous,
   * ce qui n'est pas la même chose qu'un tableau vide (aucun).
   */
  selectedMemberIds: string[] | undefined;
  selectTeam: (teamId: string | undefined) => void;
  selectMembers: (memberIds: string[] | undefined) => void;
}

const TeamSelectionContext = createContext<TeamSelectionValue | undefined>(undefined);

/**
 * Équipe et membres sélectionnés dans l'interface.
 *
 * POURQUOI UN CONTEXTE ET NON UNE REQUÊTE
 *
 * `state.team.selectedTeam` et `state.team.selectedMembers` n'ont jamais été de
 * l'état serveur : ce sont des choix de navigation, propres à l'onglet ouvert.
 * Ils étaient dans le store parce que le store était le seul endroit partagé
 * entre la barre latérale, le routeur d'équipe et les vues. React Query n'a rien
 * à en faire — un contexte, si.
 *
 * Les deux `reducers` du slice lisaient `state.teams` pour dériver la liste des
 * membres au moment de la sélection ; ce provider fait la même chose en lisant
 * `useTeamsQuery`. Il doit donc être monté À L'INTÉRIEUR du `QueryClientProvider`.
 */
export function TeamSelectionProvider({children}: {children: React.ReactNode}) {
  const {data} = useTeamsQuery();
  const teams = data?.teams;

  const [selectedTeamId, setSelectedTeamId] = useState<string | undefined>(undefined);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[] | undefined>(undefined);

  // Reprend `updateCurrentTeam` : sélectionner une équipe sélectionne d'office
  // tous ses membres.
  const selectTeam = useCallback(
    (teamId: string | undefined) => {
      if (teamId === undefined) {
        setSelectedTeamId(undefined);
        setSelectedMemberIds(undefined);
        return;
      }
      const found = teams?.find((t) => t._id === teamId);
      setSelectedTeamId(found?._id);
      setSelectedMemberIds(found?.users.map((u) => u.user));
    },
    [teams],
  );

  // Reprend `updateSelectedMembers` : on ne retient que des identifiants qui
  // appartiennent réellement à l'équipe courante.
  const selectMembers = useCallback(
    (memberIds: string[] | undefined) => {
      if (memberIds === undefined) {
        setSelectedMemberIds(undefined);
        return;
      }
      const found = teams?.find((t) => t._id === selectedTeamId);
      if (found) {
        setSelectedMemberIds(found.users.filter((u) => memberIds.includes(u.user)).map((u) => u.user));
      }
    },
    [teams, selectedTeamId],
  );

  const value = useMemo(
    () => ({selectedTeamId, selectedMemberIds, selectTeam, selectMembers}),
    [selectedTeamId, selectedMemberIds, selectTeam, selectMembers],
  );

  return <TeamSelectionContext.Provider value={value}>{children}</TeamSelectionContext.Provider>;
}

export function useTeamSelection(): TeamSelectionValue {
  const context = useContext(TeamSelectionContext);
  if (context === undefined) {
    throw new Error('useTeamSelection must be used within a TeamSelectionProvider');
  }
  return context;
}
